/**
 * Lugar, arraste e transparência do cartão do guia (as regras estão em lugar.ts).
 *
 * - **O que está embaixo** é conferido quando algo muda na tela (passo, janela que abre,
 *   maximizar, rolagem, tamanho), no máximo uma vez a cada 150 ms, e a cada meio segundo para as
 *   animações que não avisam. Conta o que se clica ou se preenche e está à vista naquele ponto
 *   (o que está atrás de um véu, não).
 * - **Arrastar** pela faixa de cima do cartão ou pela pílula, como o player do LeadTalk; o
 *   "Mover" anda com as setas. O lugar fica guardado para a pessoa; "Voltar o guia para o canto"
 *   o esquece.
 * - **Transparente** quando cobre algo que se clica, sem o mouse em cima nem o foco do teclado
 * (o do clique não conta: o botão clicado continua focado depois de sumir com o menu),
 *   e depois dos primeiros 4 s de cada passo (o texto novo é lido opaco). Quem pede menos
 *   transparência no sistema vê sempre opaco (guia.css).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { acharAncora } from './ancora';
import { useGuiaStore } from './guiaStore';
import {
  LUGAR_PADRAO,
  cruzam,
  escolherPonto,
  guardarLugar,
  lerLugar,
  limitar,
  lugarDoPonto,
  mesmoLugar,
  pontoDoLugar,
  type Lugar,
  type Obstaculo,
  type Tamanho,
} from './lugar';

const INTERATIVOS = [
  'button',
  'a[href]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  '[role="button"]',
  '[role="slider"]',
  '[role="tab"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
].join(', ');
const CAMADA = '.gp-camada';
const ESPERA_MIN = 150;
const INTERVALO = 500;
const OPACO_NO_PASSO = 4000;
const PASSO_TECLADO = 24;
/** Até aqui, o toque na pílula é clique, não arraste. */
const LIMIAR_ARRASTE = 4;

/** O foco do teclado está no cartão. */
function focoDeTeclado(el: HTMLElement): boolean {
  const ativo = document.activeElement;
  if (!ativo || !el.contains(ativo)) return false;
  try {
    return ativo.matches(':focus-visible');
  } catch {
    return true;
  }
}

function medirTela(): Tamanho {
  const raiz = document.documentElement;
  return { w: raiz.clientWidth, h: raiz.clientHeight };
}

/** O elemento aparece naquele ponto da tela (fora o próprio guia), e não atrás de um véu. */
function aVista(el: Element, r: DOMRect, tela: Tamanho): boolean {
  if (typeof document.elementFromPoint !== 'function') return true;
  const x = (Math.max(0, r.left) + Math.min(tela.w, r.right)) / 2;
  const y = (Math.max(0, r.top) + Math.min(tela.h, r.bottom)) / 2;
  let topo = document.elementFromPoint(x, y);
  if (topo?.closest(CAMADA)) {
    topo = document.elementsFromPoint(x, y).find((e) => !e.closest(CAMADA)) ?? null;
  }
  return Boolean(topo && (el === topo || el.contains(topo) || topo.contains(el)));
}

/** Não sai com a rolagem: está numa camada fixa e em nada que role. */
function criarParado(): (el: Element) => boolean {
  const raiz = document.scrollingElement ?? document.documentElement;
  const paginaParada = raiz.scrollHeight <= raiz.clientHeight + 1;
  const memo = new Map<Element, boolean>();
  const parado = (el: Element | null): boolean => {
    if (!el || el === raiz || el === document.body) return paginaParada;
    const salvo = memo.get(el);
    if (salvo !== undefined) return salvo;
    const s = getComputedStyle(el);
    const rola =
      (/auto|scroll/.test(s.overflowY) && el.scrollHeight > el.clientHeight + 1) ||
      (/auto|scroll/.test(s.overflowX) && el.scrollWidth > el.clientWidth + 1);
    const r =
      s.position === 'fixed' || s.position === 'sticky'
        ? true
        : rola
          ? false
          : parado(el.parentElement);
    memo.set(el, r);
    return r;
  };
  return parado;
}

function lerObstaculos(
  evitar: string[],
  colunas: Array<[number, number]>,
  tela: Tamanho,
): Obstaculo[] {
  const lista: Obstaculo[] = [];
  const vistos = new Set<Element>();
  for (const seletor of evitar) {
    const el = acharAncora(seletor);
    if (!el || vistos.has(el)) continue;
    vistos.add(el);
    const r = el.getBoundingClientRect();
    lista.push({ x: r.left, y: r.top, w: r.width, h: r.height, essencial: true, fixo: true });
  }
  const parado = criarParado();
  for (const el of document.querySelectorAll(INTERATIVOS)) {
    if (vistos.has(el) || el.closest(CAMADA)) continue;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height || r.bottom <= 0 || r.top >= tela.h) continue;
    if (!colunas.some(([a, b]) => r.left < b && a < r.right)) continue;
    if (el.closest('[hidden], [aria-hidden="true"], [inert]') || !aVista(el, r, tela)) continue;
    lista.push({
      x: r.left,
      y: r.top,
      w: r.width,
      h: r.height,
      essencial: false,
      fixo: parado(el),
    });
  }
  return lista;
}

interface Arraste {
  cx: number;
  cy: number;
  x0: number;
  y0: number;
  moveu: boolean;
}

export interface LugarDoCartao {
  ref: (el: HTMLElement | null) => void;
  estilo: CSSProperties;
  /** ' is-arrastando', ' is-translucido', ' is-embaixo' (o menu abre para cima). */
  classes: string;
  /** Na faixa de cima do cartão e na pílula. */
  arrastar: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: PointerEvent<HTMLElement>) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
  };
  /** No cartão e na pílula: o mouse em cima deixa opaco. */
  opaco: {
    onPointerEnter: () => void;
    onPointerLeave: () => void;
  };
  /** Setas no "Mover". */
  onTeclaMover: (e: KeyboardEvent<HTMLElement>) => void;
  /** O último toque foi um arraste (a pílula não abre). */
  foiArraste: () => boolean;
  escolhido: boolean;
  voltarAoCanto: () => void;
}

/**
 * @param evitar seletores que nem o lugar escolhido pela pessoa cobre (o que o passo aponta, o
 *   balão, os botões amarelos da janela de captura).
 * @param chave muda a cada passo: o cartão fica opaco no começo dele.
 * @param aberto o menu "⋯" ou a lista dos marcos está aberta (opaco).
 */
export function useLugarDoCartao(evitar: string[], chave: string, aberto: boolean): LugarDoCartao {
  const userId = useGuiaStore((s) => s.userId);
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [escolhido, setEscolhido] = useState<Lugar | null>(() => lerLugar(userId));
  const [lugar, setLugar] = useState<Lugar>(() => escolhido ?? LUGAR_PADRAO);
  const [cobrindo, setCobrindo] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [emCima, setEmCima] = useState(false);
  const [foco, setFoco] = useState(false);
  const [noComeco, setNoComeco] = useState(true);

  const lugarRef = useRef(lugar);
  const escolhidoRef = useRef(escolhido);
  const evitarRef = useRef(evitar);
  const arrasteRef = useRef<Arraste | null>(null);
  const foiArrasteRef = useRef(false);
  evitarRef.current = evitar;
  const evitarChave = evitar.join('\n');

  const mudarLugar = useCallback((l: Lugar): void => {
    lugarRef.current = l;
    setLugar(l);
  }, []);

  const escolher = useCallback(
    (l: Lugar | null): void => {
      escolhidoRef.current = l;
      setEscolhido(l);
      guardarLugar(userId, l);
    },
    [userId],
  );

  useEffect(() => {
    const salvo = lerLugar(userId);
    escolhidoRef.current = salvo;
    setEscolhido(salvo);
  }, [userId]);

  const avaliar = useCallback((): void => {
    if (!el || arrasteRef.current?.moveu) return;
    setFoco(focoDeTeclado(el));
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const tela = medirTela();
    const cartao = { w: r.width, h: r.height };
    const base = pontoDoLugar(escolhidoRef.current ?? LUGAR_PADRAO, cartao, tela);
    const atual = pontoDoLugar(lugarRef.current, cartao, tela);
    const outroX = tela.w - base.x - cartao.w;
    const obstaculos = lerObstaculos(
      evitarRef.current,
      [
        [base.x, base.x + cartao.w],
        [outroX, outroX + cartao.w],
        [atual.x, atual.x + cartao.w],
      ],
      tela,
    );
    const p = escolherPonto({
      base,
      atual,
      escolhido: Boolean(escolhidoRef.current),
      cartao,
      tela,
      obstaculos,
    });
    const novo = lugarDoPonto(p, cartao, tela);
    if (!mesmoLugar(novo, lugarRef.current)) mudarLugar(novo);
    const caixa = { x: p.x, y: p.y, w: cartao.w, h: cartao.h };
    setCobrindo(obstaculos.some((o) => cruzam(caixa, o)));
  }, [el, mudarLugar]);

  // Antes de pintar: o cartão já aparece no lugar certo.
  useLayoutEffect(() => {
    avaliar();
  }, [avaliar, chave, evitarChave, escolhido]);

  useEffect(() => {
    if (!el) return undefined;
    let quadro = 0;
    let espera = 0;
    let ultimo = 0;
    const agendar = (): void => {
      if (quadro || espera) return;
      const falta = ESPERA_MIN - (performance.now() - ultimo);
      if (falta > 0) {
        espera = window.setTimeout(() => {
          espera = 0;
          agendar();
        }, falta);
        return;
      }
      quadro = requestAnimationFrame(() => {
        quadro = 0;
        ultimo = performance.now();
        avaliar();
      });
    };
    // As mudanças do próprio cartão (lugar, transparência) não contam.
    const mo = new MutationObserver((registros) => {
      if (registros.some((m) => !el.contains(m.target))) agendar();
    });
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open'],
    });
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(agendar) : null;
    ro?.observe(el);
    window.addEventListener('resize', agendar);
    window.addEventListener('scroll', agendar, true);
    const intervalo = window.setInterval(agendar, INTERVALO);
    let focoEspera = 0;
    const verFoco = (): void => {
      window.clearTimeout(focoEspera);
      focoEspera = window.setTimeout(() => setFoco(focoDeTeclado(el)), 0);
    };
    document.addEventListener('focusin', verFoco);
    document.addEventListener('focusout', verFoco);
    return () => {
      cancelAnimationFrame(quadro);
      window.clearTimeout(espera);
      window.clearInterval(intervalo);
      window.clearTimeout(focoEspera);
      document.removeEventListener('focusin', verFoco);
      document.removeEventListener('focusout', verFoco);
      mo.disconnect();
      ro?.disconnect();
      window.removeEventListener('resize', agendar);
      window.removeEventListener('scroll', agendar, true);
    };
  }, [el, avaliar]);

  useEffect(() => {
    setNoComeco(true);
    const t = window.setTimeout(() => setNoComeco(false), OPACO_NO_PASSO);
    return () => window.clearTimeout(t);
  }, [chave]);

  const onPointerDown = (e: PointerEvent<HTMLElement>): void => {
    if (e.button !== 0 || !el) return;
    const botao = (e.target as HTMLElement).closest('button');
    if (botao && botao !== e.currentTarget && !botao.hasAttribute('data-mover')) return;
    const r = el.getBoundingClientRect();
    arrasteRef.current = { cx: e.clientX, cy: e.clientY, x0: r.left, y0: r.top, moveu: false };
    foiArrasteRef.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>): void => {
    const a = arrasteRef.current;
    if (!a || !el) return;
    const dx = e.clientX - a.cx;
    const dy = e.clientY - a.cy;
    if (!a.moveu) {
      if (Math.hypot(dx, dy) < LIMIAR_ARRASTE) return;
      a.moveu = true;
      setArrastando(true);
    }
    const r = el.getBoundingClientRect();
    const cartao = { w: r.width, h: r.height };
    const tela = medirTela();
    mudarLugar(lugarDoPonto(limitar({ x: a.x0 + dx, y: a.y0 + dy }, cartao, tela), cartao, tela));
  };

  const soltar = (): void => {
    const a = arrasteRef.current;
    arrasteRef.current = null;
    if (!a?.moveu) return;
    foiArrasteRef.current = true;
    setArrastando(false);
    escolher(lugarRef.current);
  };

  const onTeclaMover = (e: KeyboardEvent<HTMLElement>): void => {
    const passos: Record<string, [number, number]> = {
      ArrowLeft: [-PASSO_TECLADO, 0],
      ArrowRight: [PASSO_TECLADO, 0],
      ArrowUp: [0, -PASSO_TECLADO],
      ArrowDown: [0, PASSO_TECLADO],
    };
    const passo = passos[e.key];
    if (!passo || !el) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    const cartao = { w: r.width, h: r.height };
    const tela = medirTela();
    const p = limitar({ x: r.left + passo[0], y: r.top + passo[1] }, cartao, tela);
    const l = lugarDoPonto(p, cartao, tela);
    mudarLugar(l);
    escolher(l);
  };

  const translucido = cobrindo && !emCima && !foco && !arrastando && !aberto && !noComeco;
  const estilo: CSSProperties =
    lugar.h === 'dir' ? { right: lugar.x, left: 'auto' } : { left: lugar.x, right: 'auto' };
  if (lugar.v === 'baixo') Object.assign(estilo, { bottom: lugar.y, top: 'auto' });
  else Object.assign(estilo, { top: lugar.y, bottom: 'auto' });

  return {
    ref: setEl,
    estilo,
    classes: `${arrastando ? ' is-arrastando' : ''}${translucido ? ' is-translucido' : ''}${
      lugar.v === 'baixo' ? ' is-embaixo' : ''
    }`,
    arrastar: { onPointerDown, onPointerMove, onPointerUp: soltar, onPointerCancel: soltar },
    opaco: {
      onPointerEnter: () => setEmCima(true),
      onPointerLeave: () => setEmCima(false),
    },
    onTeclaMover,
    foiArraste: () => foiArrasteRef.current,
    escolhido: Boolean(escolhido),
    // Volta mesmo: sem isto, o cartão ficaria onde está, se ali estivesse livre (lugar.ts, 2).
    voltarAoCanto: () => {
      mudarLugar(LUGAR_PADRAO);
      escolher(null);
    },
  };
}
